/**
 * Dynamic Collage Component
 * Connects with featured product to display its images
 */

// Debug mode - set to true for verbose logging
const DEBUG_MODE = false;

// Helper function for logging
function logDebug(...args) {
  if (DEBUG_MODE || (window.SpiralDirect && window.SpiralDirect.debug)) {
    console.log('[DynamicCollage]', ...args);
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', function() {
  initDynamicCollage();
  
  // Check if we already have featured product data and use it
  if (window.spiralDirectFeaturedProduct && window.spiralDirectFeaturedProduct.handle) {
    updateCollageWithProductImages(window.spiralDirectFeaturedProduct);
  }
});

function initDynamicCollage() {
  // Find all dynamic collage sections
  const collageSections = document.querySelectorAll('.dynamic-collage-section');
  
  if (collageSections.length === 0) return;
  
  // Set up event listener for featured product updates
  document.addEventListener('spiral:featured-product-updated', function(event) {
    updateCollageWithProductImages(event.detail);
  });
}

function updateCollageWithProductImages(productData) {
  if (!productData || !productData.handle) return;
  
  logDebug('Updating collage with product images for:', productData.handle);
  
  // Get all collage sections that are set to use featured product images
  const collageSections = document.querySelectorAll('.dynamic-collage-section[data-use-featured-product-images="true"]');
  
  if (collageSections.length === 0) {
    logDebug('No dynamic collage sections found with featured product image setting enabled');
    return;
  }
  
  // Fetch the product data to get images
  fetch(`/products/${productData.handle}.js`)
    .then(response => response.json())
    .then(product => {
      logDebug('Product data retrieved for collage:', product);
      
      if (!product.images || product.images.length === 0) {
        logDebug('No images found in product data');
        
        // If no images are available, handle blocks according to settings
        collageSections.forEach(section => {
          const hideWhenNoImages = section.getAttribute('data-hide-when-no-images') === 'true';
          const removeWhenNoImages = section.getAttribute('data-remove-when-no-images') === 'true';
          
          if (hideWhenNoImages || removeWhenNoImages) {
            logDebug('Processing all image blocks due to no product images');
            const imageBlocks = section.querySelectorAll('.collage__item--image');
            
            imageBlocks.forEach(block => {
              if (removeWhenNoImages) {
                logDebug('Removing block from DOM as no product images are available');
                block.remove(); // Remove from DOM
              } else if (hideWhenNoImages) {
                logDebug('Hiding block with CSS as no product images are available');
                block.style.display = 'none';
              }
            });
          }
        });
        
        return;
      }
      
      // Update image position indicators if present
      updateImagePositionIndicators(product.images.length);
      
      collageSections.forEach(section => {
        logDebug('Updating collage section:', section.getAttribute('data-section-id'));
        
        const hideWhenNoImages = section.getAttribute('data-hide-when-no-images') === 'true';
        const removeWhenNoImages = section.getAttribute('data-remove-when-no-images') === 'true';
        const useEqualColumnsForTwoImages = section.getAttribute('data-use-equal-columns-for-two-images') === 'true';
        
        // Check if we should use equal columns layout for two images
        if (useEqualColumnsForTwoImages && product.images.length === 2) {
          logDebug('Using equal 50/50 columns layout for exactly two images');
          adjustLayoutForTwoImages(section);
        } else {
          // Reset any previously applied equal columns styles
          resetColumnLayout(section);
        }
        
        // Reset visibility of all blocks before updating
        if (hideWhenNoImages || removeWhenNoImages) {
          // Store blocks that need to be removed to avoid modifying the NodeList during iteration
          const blocksToRemove = [];
          
          section.querySelectorAll('.collage__item--image').forEach((block, index) => {
            // Initially show all blocks, then hide/remove them if needed in updateCollageSectionImages
            if (!removeWhenNoImages) {
              block.style.display = '';
            }
            
            // Pre-check if this block should be hidden/removed based on its index or specified position
            const collageCard = block.querySelector('.collage-card');
            if (collageCard) {
              const specifiedPosition = parseInt(collageCard.getAttribute('data-featured-product-image-index')) || (index + 1);
              // If the specified position is beyond available images and no fallback is possible
              if (specifiedPosition > product.images.length && index >= product.images.length) {
                logDebug(`Pre-processing block ${index}: Position ${specifiedPosition} exceeds available images ${product.images.length}`);
                
                if (removeWhenNoImages) {
                  blocksToRemove.push(block);
                } else if (hideWhenNoImages) {
                  block.style.display = 'none';
                }
              }
            }
          });
          
          // Remove blocks that need to be removed
          if (removeWhenNoImages) {
            blocksToRemove.forEach(block => {
              logDebug('Removing block from DOM as it exceeds available images');
              block.remove();
            });
          }
        }
        
        updateCollageSectionImages(section, product);
      });
    })
    .catch(error => {
      console.error('Error fetching product data for collage:', error);
    });
}

/**
 * Updates the image position indicators to show warnings when selected positions exceed available images
 */
function updateImagePositionIndicators(totalProductImages) {
  // Find all image position indicators
  document.querySelectorAll('.image-position-indicator').forEach(indicator => {
    const card = indicator.closest('.collage-card');
    if (!card) return;
    
    const requestedPosition = parseInt(card.getAttribute('data-featured-product-image-index')) || 1;
    
    // Check if the requested position exceeds the available images
    if (requestedPosition > totalProductImages) {
      indicator.innerHTML = `<span>Image ${requestedPosition} (Fallback: Image ${(requestedPosition % totalProductImages) || totalProductImages})</span>`;
      indicator.style.backgroundColor = 'rgba(204, 51, 0, 0.8)'; // Red background for warning
    } else {
      indicator.innerHTML = `<span>Image ${requestedPosition}</span>`;
      indicator.style.backgroundColor = 'rgba(0, 0, 0, 0.6)'; // Normal background
    }
  });
}

function updateCollageSectionImages(section, product) {
  // Find image blocks in this section
  const imageBlocks = section.querySelectorAll('.collage__item--image');
  
  if (imageBlocks.length === 0) {
    logDebug('No image blocks found in this section');
    return;
  }
  
  logDebug(`Found ${imageBlocks.length} image blocks to update`);
  
  // Check visibility settings
  const hideWhenNoImages = section.getAttribute('data-hide-when-no-images') === 'true';
  const removeWhenNoImages = section.getAttribute('data-remove-when-no-images') === 'true';
  const useEqualColumnsForTwoImages = section.getAttribute('data-use-equal-columns-for-two-images') === 'true';
  const totalImages = product.images.length;
  
  logDebug(`Product has ${totalImages} images, hide when no images: ${hideWhenNoImages}, remove when no images: ${removeWhenNoImages}, use equal columns: ${useEqualColumnsForTwoImages}`);
  
  // Store blocks that need to be removed to avoid modifying the NodeList during iteration
  const blocksToRemove = [];
  
  // Keep track of visible blocks for layout adjustment
  let visibleBlocksCount = 0;
  
  // Distribute product images among the image blocks
  imageBlocks.forEach((block, index) => {
    // Get the collage-card element within this block
    const collageCard = block.querySelector('.collage-card');
    if (!collageCard) {
      logDebug(`Block ${index}: No collage-card element found`);
      return;
    }
    
    // Direct check for blocks beyond the number of available images
    if (index >= totalImages) {
      if (removeWhenNoImages) {
        logDebug(`Block ${index}: Marking block for removal as its index exceeds total images (${totalImages})`);
        blocksToRemove.push(block);
        return;
      } else if (hideWhenNoImages) {
        logDebug(`Block ${index}: Directly hiding block as its index exceeds total images (${totalImages})`);
        block.style.display = 'none';
        return;
      }
    } else {
      // This block will be visible
      visibleBlocksCount++;
    }
    
    // Try to get the preferred image position from the card's data attribute
    // Note: We're using 1-based indexing in the UI (1=first image, 2=second image)
    // but need to convert to 0-based for JavaScript array indexing
    let imagePosition = parseInt(collageCard.getAttribute('data-featured-product-image-index'));
    
    // Convert from 1-based UI position to 0-based array index
    let imageIndex = imagePosition ? imagePosition - 1 : 0;
    
    // If no valid index or index is out of bounds, use the block index as fallback
    // But only if the block index is within the range of available images
    if (isNaN(imageIndex) || imageIndex < 0 || imageIndex >= product.images.length) {
      logDebug(`Block ${index}: Invalid image position ${imagePosition} (index ${imageIndex}), checking if we can use block index as fallback`);
      
      // Only use the block index as fallback if it's within the range of available images
      // Otherwise, handle according to settings
      if (index < product.images.length) {
        imageIndex = index;
        logDebug(`Block ${index}: Using block index as fallback`);
      } else if (removeWhenNoImages) {
        logDebug(`Block ${index}: Block index ${index} exceeds available images count ${product.images.length}, marking for removal`);
        blocksToRemove.push(block);
        visibleBlocksCount--;
        return;
      } else if (hideWhenNoImages) {
        logDebug(`Block ${index}: Block index ${index} exceeds available images count ${product.images.length}, hiding block`);
        block.style.display = 'none';
        visibleBlocksCount--;
        return;
      } else {
        // If we shouldn't hide or remove the block but there's no image for it, just use the last available image
        imageIndex = (product.images.length - 1) % product.images.length;
        logDebug(`Block ${index}: Using image at index ${imageIndex} as fallback`);
      }
    } else {
      logDebug(`Block ${index}: Using specified image position ${imagePosition} (index ${imageIndex})`);
    }
    
    const productImage = product.images[imageIndex];
    
    if (!productImage) {
      logDebug(`Block ${index}: No product image found at index ${imageIndex}`);
      
      // Handle according to settings when no image is available
      if (removeWhenNoImages) {
        logDebug(`Block ${index}: Marking block for removal due to no available image`);
        blocksToRemove.push(block);
        visibleBlocksCount--;
      } else if (hideWhenNoImages) {
        logDebug(`Block ${index}: Hiding block due to no available image`);
        block.style.display = 'none';
        visibleBlocksCount--;
      }
      
      return;
    }
    
    // Ensure the block is visible (in case it was hidden before)
    if (block.style.display === 'none') {
      block.style.display = '';
    }
    
    // Find the image element in the block
    const imgElement = block.querySelector('img:not(.placeholder-svg)');
    const placeholderElement = block.querySelector('.placeholder-svg');
    
    if (imgElement) {
      logDebug(`Block ${index}: Updating existing image element with product image position ${imagePosition}`);
      // Update existing image
      updateImageElement(imgElement, productImage, product);
    } else if (placeholderElement) {
      logDebug(`Block ${index}: Replacing placeholder with real image from product image position ${imagePosition}`);
      // Replace placeholder with real image
      replacePlaceholderWithImage(placeholderElement, productImage, product);
    } else {
      logDebug(`Block ${index}: No image or placeholder element found`);
    }
  });
  
  // Remove blocks that need to be removed after the loop
  if (removeWhenNoImages && blocksToRemove.length > 0) {
    logDebug(`Removing ${blocksToRemove.length} blocks from the DOM`);
    blocksToRemove.forEach(block => block.remove());
  }
  
  // Apply equal columns layout if appropriate
  if (useEqualColumnsForTwoImages && visibleBlocksCount === 2 && totalImages === 2) {
    logDebug('Applying equal columns layout for two images');
    section.classList.add('collage--equal-columns');
  } else {
    // Reset to default layout
    section.classList.remove('collage--equal-columns');
  }
}

function updateImageElement(imgElement, productImage, product) {
  // For Shopify images, ensure we're using the correct format
  const imageSrc = productImage.src || productImage;
  
  // Convert image URL to proper format if needed
  // Shopify images often contain "_small", "_medium", etc. which we need to remove
  const cleanImageUrl = imageSrc.replace(/(_(small|medium|large|grande|1024x1024|2048x2048|master)\.)/, '.');
  
  // Update image attributes
  imgElement.src = cleanImageUrl;
  imgElement.srcset = generateSrcSet(cleanImageUrl);
  imgElement.alt = product.title || 'Product image';
  
  // Update parent element's aspect ratio if it has style with --ratio-percent
  const mediaParent = imgElement.closest('.media');
  if (mediaParent) {
    if (productImage.width && productImage.height) {
      // If we have dimensions, use them
      const aspectRatio = productImage.width / productImage.height;
      mediaParent.style.setProperty('--ratio-percent', `${(1 / aspectRatio) * 100}%`);
    } else {
      // Default to square if no dimensions
      mediaParent.style.setProperty('--ratio-percent', '100%');
    }
  }
}

function replacePlaceholderWithImage(placeholderElement, productImage, product) {
  const parent = placeholderElement.parentNode;
  
  // For Shopify images, ensure we're using the correct format
  const imageSrc = productImage.src || productImage;
  
  // Convert image URL to proper format if needed
  const cleanImageUrl = imageSrc.replace(/(_(small|medium|large|grande|1024x1024|2048x2048|master)\.)/, '.');
  
  // Create new image element
  const newImg = document.createElement('img');
  newImg.src = cleanImageUrl;
  newImg.srcset = generateSrcSet(cleanImageUrl);
  newImg.alt = product.title || 'Product image';
  newImg.loading = 'lazy';
  
  // Set up other necessary attributes
  newImg.classList.add('motion-reduce');
  
  // Replace placeholder with the new image
  parent.replaceChild(newImg, placeholderElement);
  
  // Update parent's aspect ratio
  const mediaParent = parent.closest('.media');
  if (mediaParent) {
    if (productImage.width && productImage.height) {
      const aspectRatio = productImage.width / productImage.height;
      mediaParent.style.setProperty('--ratio-percent', `${(1 / aspectRatio) * 100}%`);
    } else {
      // Default to square if no dimensions
      mediaParent.style.setProperty('--ratio-percent', '100%');
    }
  }
}

function generateSrcSet(imageSrc) {
  // Parse the image URL to format for Shopify's CDN
  // Remove any existing size parameters if present
  const baseUrl = imageSrc.split('?')[0].replace(/(_(small|medium|large|grande|1024x1024|2048x2048|master)\.)/, '.');
  
  // Generate srcset with various sizes using Shopify's image URL format
  return [
    `${baseUrl.replace(/\.(jpg|jpeg|png|gif|webp)$/, '_550x.$1')} 550w`,
    `${baseUrl.replace(/\.(jpg|jpeg|png|gif|webp)$/, '_720x.$1')} 720w`,
    `${baseUrl.replace(/\.(jpg|jpeg|png|gif|webp)$/, '_990x.$1')} 990w`,
    `${baseUrl.replace(/\.(jpg|jpeg|png|gif|webp)$/, '_1100x.$1')} 1100w`,
    `${baseUrl.replace(/\.(jpg|jpeg|png|gif|webp)$/, '_1500x.$1')} 1500w`,
    `${baseUrl.replace(/\.(jpg|jpeg|png|gif|webp)$/, '_2200x.$1')} 2200w`,
    `${baseUrl.replace(/\.(jpg|jpeg|png|gif|webp)$/, '_3000x.$1')} 3000w`
  ].join(', ');
}

/**
 * Adjusts the layout to equal 50/50 columns when there are exactly two images
 */
function adjustLayoutForTwoImages(section) {
  const imageBlocks = section.querySelectorAll('.collage__item--image');
  
  // Only proceed if we have exactly two visible image blocks
  const visibleBlocks = Array.from(imageBlocks).filter(block => block.style.display !== 'none');
  if (visibleBlocks.length !== 2) {
    logDebug(`Found ${visibleBlocks.length} visible blocks, expected 2 for equal columns layout`);
    return;
  }
  
  logDebug('Adjusting layout to equal 50/50 columns for two images');
  
  // Add a class to the section to indicate we're using equal columns
  section.classList.add('collage--equal-columns');
  
  // Apply equal width styles to the section
  section.style.display = 'flex';
  section.style.flexWrap = 'wrap';
  section.style.justifyContent = 'center';
  section.style.gap = '1rem';
  
  // Add custom styles for equal columns if they don't exist yet
  if (!document.getElementById('equal-columns-style')) {
    const styleElement = document.createElement('style');
    styleElement.id = 'equal-columns-style';
    styleElement.textContent = `
      .collage--equal-columns {
        justify-content: center !important;
        display: flex !important;
        flex-wrap: wrap !important;
        gap: 1rem !important;
      }
      
      .collage--equal-columns .collage__item--image {
        flex-basis: 50% !important;
        width: 50% !important;
        max-width: 50% !important;
        margin: 0 auto !important;
      }
      
      .collage--equal-columns .collage__item--image .media {
        margin: 0 auto !important;
        width: 100% !important;
        max-width: 95% !important;
      }
      
      @media screen and (min-width: 750px) {
        .collage--equal-columns .collage__item--left:nth-child(2),
        .collage--equal-columns .collage__item--right:nth-child(2) {
          grid-column: span 1 !important;
        }
        
        .collage--equal-columns .collage__item--image {
          max-width: 48% !important;
        }
        
        .collage--equal-columns .collage__item--image .media {
          max-width: 100% !important;
        }
      }
    `;
    document.head.appendChild(styleElement);
  }
  
  // Apply equal width styles to each block
  visibleBlocks.forEach((block, index) => {
    // Basic layout fixes
    block.style.flexBasis = '50%';
    block.style.width = '50%';
    block.style.maxWidth = '50%';
    block.style.margin = '0 auto';
    
    // Center the media content inside
    const mediaElement = block.querySelector('.media');
    if (mediaElement) {
      mediaElement.style.margin = '0 auto';
      mediaElement.style.width = '100%';
      mediaElement.style.maxWidth = '95%';
    }
    
    // Remove any specific grid column setting that might interfere
    block.style.gridColumn = '';
    
    logDebug(`Applied equal width and centering styles to block ${index}`);
  });
}

/**
 * Resets column layout back to default
 */
function resetColumnLayout(section) {
  if (section.classList.contains('collage--equal-columns')) {
    logDebug('Resetting column layout to default');
    
    // Remove the class that indicates equal columns
    section.classList.remove('collage--equal-columns');
    
    // Reset the styles on the section
    section.style.display = '';
    section.style.flexWrap = '';
    section.style.justifyContent = '';
    section.style.gap = '';
    
    // Reset the styles on each block
    const imageBlocks = section.querySelectorAll('.collage__item--image');
    imageBlocks.forEach((block, index) => {
      block.style.flexBasis = '';
      block.style.width = '';
      block.style.maxWidth = '';
      block.style.gridColumn = '';
      block.style.margin = '';
      
      // Reset the media element styles
      const mediaElement = block.querySelector('.media');
      if (mediaElement) {
        mediaElement.style.margin = '';
        mediaElement.style.width = '';
        mediaElement.style.maxWidth = '';
      }
      
      logDebug(`Reset layout styles for block ${index}`);
    });
  }
}
